import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MapPin, Camera, MessageSquare, LogOut, Upload, X, Settings, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import CameraCapture from '@/components/CameraCapture';
import ProfileSettings from '@/components/ProfileSettings';
import ChatThread from '@/components/ChatThread';
import DeadlineCountdown from '@/components/DeadlineCountdown';
import ExtensionRequestDialog from '@/components/ExtensionRequestDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FieldStaffDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showWorkLogForm, setShowWorkLogForm] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('tasks');

  useEffect(() => {
    fetchTasks();
    fetchMessages();
    fetchUnreadCount();
    
    // Poll for tasks and unread count every 30 seconds (lighter load)
    const interval = setInterval(() => {
      fetchTasks();
      fetchUnreadCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchMessages = async () => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      const response = await axios.get(`${API}/messages`, config);
      setMessages(response.data);
      fetchUnreadCount(); // Refresh unread count when messages are fetched
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      const response = await axios.get(`${API}/messages/unread-count`, config);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      const response = await axios.get(`${API}/tasks`, config);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const submitWorkLog = async (formData) => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      await axios.post(`${API}/work-logs`, formData, config);
      toast.success('Work log submitted successfully');
      setShowWorkLogForm(false);
      fetchTasks();
    } catch (error) {
      console.error('Error submitting work log:', error);
      toast.error('Failed to submit work log');
    }
  };

  const sendMessage = async (formData) => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      await axios.post(`${API}/messages`, formData, config);
      toast.success('Message sent successfully');
      setShowMessageForm(false);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const closeTask = async (taskId) => {
    try {
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };
      await axios.patch(`${API}/tasks/${taskId}`, { status: 'completed' }, config);
      toast.success('Task closed successfully');
      fetchTasks();
    } catch (error) {
      console.error('Error closing task:', error);
      toast.error('Failed to close task');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300'
    };
    return variants[status] || variants.pending;
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">FieldMate</h1>
              <p className="text-sm text-slate-500">Welcome, {user?.name}</p>
            </div>
            <span className="ml-4 bg-red-600 text-white px-3 py-1 rounded text-sm font-bold">
              Unread: {unreadCount}
            </span>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowProfileSettings(true)}
              variant="outline"
              data-testid="profile-settings-btn"
              className="gap-2"
            >
              <Settings className="w-4 h-4" />
              Profile
            </Button>
            <Button 
              onClick={logout} 
              variant="outline"
              data-testid="logout-btn"
              className="gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs for Tasks and Messages */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="tasks" data-testid="tasks-tab">
              <ClipboardList className="w-4 h-4 mr-2" />
              My Tasks
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="messages-tab">
              <MessageSquare className="w-4 h-4 mr-2" />
              Messages
              <span className={`ml-2 inline-flex items-center justify-center ${unreadCount > 0 ? 'bg-red-600' : 'bg-gray-400'} text-white rounded-full px-2 py-1 text-xs font-bold min-w-[20px]`}>
                {unreadCount}
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tasks">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="p-6 bg-yellow-50 border-yellow-200">
            <p className="text-sm text-slate-600 mb-1">Pending</p>
            <p className="text-3xl font-bold text-yellow-700">{pendingTasks.length}</p>
          </Card>
          <Card className="p-6 bg-blue-50 border-blue-200">
            <p className="text-sm text-slate-600 mb-1">In Progress</p>
            <p className="text-3xl font-bold text-blue-700">{inProgressTasks.length}</p>
          </Card>
          <Card className="p-6 bg-green-50 border-green-200">
            <p className="text-sm text-slate-600 mb-1">Completed</p>
            <p className="text-3xl font-bold text-green-700">{completedTasks.length}</p>
          </Card>
        </div>

        {/* Tasks */}
        <div className="space-y-8">
          {pendingTasks.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Pending Tasks</h2>
              <div className="grid gap-4">
                {pendingTasks.map(task => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    onSelect={setSelectedTask}
                    onWorkLog={() => { setSelectedTask(task); setShowWorkLogForm(true); }}
                    onMessage={() => { setSelectedChat(task); setActiveTab('messages'); }}
                    onClose={closeTask}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </div>
            </div>
          )}

          {inProgressTasks.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">In Progress</h2>
              <div className="grid gap-4">
                {inProgressTasks.map(task => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    onSelect={setSelectedTask}
                    onWorkLog={() => { setSelectedTask(task); setShowWorkLogForm(true); }}
                    onMessage={() => { setSelectedChat(task); setActiveTab('messages'); }}
                    onClose={closeTask}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </div>
            </div>
          )}

          {completedTasks.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Completed</h2>
              <div className="grid gap-4">
                {completedTasks.map(task => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    onSelect={setSelectedTask}
                    getStatusBadge={getStatusBadge}
                    readOnly
                  />
                ))}
              </div>
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="messages">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Messages & Conversations</h2>
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Task List with Messages */}
              <div>
                <h3 className="font-semibold text-slate-700 mb-3">My Conversations</h3>
                <div className="space-y-2">
                  {tasks.filter(t => {
                    const taskMessages = messages.filter(m => m.task_id === t.id);
                    return taskMessages.length > 0;
                  }).map(task => {
                    const taskMessages = messages.filter(m => m.task_id === task.id);
                    const unreadMessages = taskMessages.filter(m => m.to_user_id === user.id && m.read_status === false);
                    const lastMessage = taskMessages[taskMessages.length - 1];
                    return (
                      <Card 
                        key={task.id}
                        className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${
                          selectedChat?.id === task.id ? 'border-teal-500 border-2' : ''
                        }`}
                        onClick={() => setSelectedChat(task)}
                        data-testid={`chat-task-${task.id}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-bold text-slate-900">{task.title}</h4>
                          <div className="flex gap-2">
                            <Badge className="bg-teal-100 text-teal-800">
                              {taskMessages.length} total
                            </Badge>
                            {unreadMessages.length > 0 && (
                              <Badge className="bg-red-600 text-white">
                                {unreadMessages.length} unread
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-slate-600">Chat with Admin</p>
                        {lastMessage && (
                          <p className="text-xs text-slate-400 mt-2">
                            Last: {new Date(lastMessage.timestamp).toLocaleString()}
                          </p>
                        )}
                      </Card>
                    );
                  })}
                  {tasks.filter(t => messages.filter(m => m.task_id === t.id).length > 0).length === 0 && (
                    <p className="text-slate-500 text-center py-8">No conversations yet</p>
                  )}
                </div>
              </div>

              {/* Chat Thread */}
              <div>
                {selectedChat ? (
                  <Card className="h-[600px] flex flex-col">
                    <ChatThread 
                      task={selectedChat}
                      currentUser={user}
                      onMessageSent={() => {
                        fetchMessages();
                        fetchUnreadCount();
                      }}
                    />
                  </Card>
                ) : (
                  <Card className="h-[600px] flex items-center justify-center">
                    <div className="text-center text-slate-500">
                      <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                      <p>Select a conversation to start chatting</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Work Log Form Dialog */}
      {showWorkLogForm && selectedTask && (
        <Dialog open={showWorkLogForm} onOpenChange={(open) => { if (!open) setShowWorkLogForm(false); }} modal={true}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
            <WorkLogForm task={selectedTask} onSubmit={submitWorkLog} onCancel={() => setShowWorkLogForm(false)} />
          </DialogContent>
        </Dialog>
      )}

      {/* Message Form Dialog */}
      {showMessageForm && selectedTask && (
        <Dialog open={showMessageForm} onOpenChange={(open) => { if (!open) setShowMessageForm(false); }} modal={true}>
          <DialogContent onInteractOutside={(e) => e.preventDefault()}>
            <MessageForm task={selectedTask} onSubmit={sendMessage} onCancel={() => setShowMessageForm(false)} />
          </DialogContent>
        </Dialog>
      )}

      {/* Profile Settings Dialog */}
      {showProfileSettings && (
        <ProfileSettings 
          user={user}
          onClose={() => setShowProfileSettings(false)}
          onUpdate={(updatedUser) => {
            setUser(updatedUser);
            checkAuth();
          }}
        />
      )}
    </div>
  );
};

const TaskCard = ({ task, onSelect, onWorkLog, onMessage, onClose, getStatusBadge, readOnly }) => {
  return (
    <Card className="p-6" data-testid={`task-card-${task.id}`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-bold text-slate-900">{task.title}</h3>
              <Badge className={`${getStatusBadge(task.status)} border`}>
                {task.status.replace('_', ' ')}
              </Badge>
            </div>
            {task.deadline && (
              <DeadlineCountdown deadline={task.deadline} status={task.status} />
            )}
          </div>
          <p className="text-slate-600 mb-3">{task.description}</p>
          {task.extension_requested && (
            <div className="mb-3">
              <Badge className="bg-orange-100 text-orange-800">
                Extension pending approval
              </Badge>
            </div>
          )}
          {task.customer_name && (
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm font-semibold text-slate-900">Customer: {task.customer_name}</p>
              {task.customer_details && <p className="text-sm text-slate-600">{task.customer_details}</p>}
            </div>
          )}
        </div>
      </div>

      {!readOnly && (
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={onWorkLog}
            data-testid={`mark-location-btn-${task.id}`}
            className="bg-teal-600 hover:bg-teal-700 gap-2"
          >
            <MapPin className="w-4 h-4" />
            Mark Location
          </Button>
          <Button 
            onClick={onMessage}
            data-testid={`send-message-btn-${task.id}`}
            variant="outline"
            className="gap-2"
          >
            <MessageSquare className="w-4 h-4" />
            Message Admin
          </Button>
          {task.deadline && task.status !== 'completed' && (
            <ExtensionRequestDialog task={task} onSuccess={() => window.location.reload()} />
          )}
          {task.status !== 'completed' && (
            <Button 
              onClick={() => onClose(task.id)}
              data-testid={`close-task-btn-${task.id}`}
              variant="outline"
              className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
            >
              Close Task
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

const WorkLogForm = ({ task, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    task_id: task.id,
    gps_latitude: null,
    gps_longitude: null,
    photos: [],
    notes: '',
    customer_name: task.customer_name || '',
    customer_details: task.customer_details || ''
  });
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const getLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            gps_latitude: position.coords.latitude,
            gps_longitude: position.coords.longitude
          });
          setGettingLocation(false);
          toast.success('Location captured');
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast.error('Failed to get location. Please enable location services.');
          setGettingLocation(false);
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
      setGettingLocation(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    });
    
    // Reset file input to allow selecting the same file again and prevent duplicate triggers
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setFormData({
      ...formData,
      photos: formData.photos.filter((_, i) => i !== index)
    });
  };

  const handleCameraCapture = (imageData) => {
    console.log('Photo captured from camera, adding to form');
    setFormData(prev => ({
      ...prev,
      photos: [...prev.photos, imageData]
    }));
    setShowCamera(false);
    toast.success('Photo captured successfully!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // Prevent multiple submissions
    
    setSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Camera Full Screen Overlay - Renders outside form */}
      {showCamera && (
        <CameraCapture 
          onCapture={handleCameraCapture} 
          onClose={() => {
            console.log('Camera closed');
            setShowCamera(false);
          }} 
        />
      )}
      
      {/* Form Content */}
      <form onSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle>Mark Work Location</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 mt-4">
        <div>
          <label className="text-sm font-medium mb-2 block">GPS Location</label>
          <div className="flex gap-2">
            <Button 
              type="button" 
              onClick={getLocation} 
              disabled={gettingLocation}
              data-testid="get-location-btn"
              className="bg-teal-600 hover:bg-teal-700"
            >
              {gettingLocation ? 'Getting Location...' : 'Get Current Location'}
            </Button>
            {formData.gps_latitude && formData.gps_longitude && (
              <div className="flex items-center text-sm text-green-600">
                <MapPin className="w-4 h-4 mr-1" />
                Location captured
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Capture or Upload Photos</label>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              type="button"
              onClick={() => {
                console.log('Opening camera...');
                setShowCamera(true);
              }}
              className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-teal-400 transition-colors cursor-pointer"
              data-testid="open-camera-btn"
            >
              <Camera className="w-8 h-8 mx-auto text-teal-600 mb-2" />
              <p className="text-xs text-slate-600 font-medium">Open Camera</p>
            </button>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-teal-400 transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                data-testid="photo-upload-input"
                className="hidden"
                id="photo-upload"
              />
              <label htmlFor="photo-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-xs text-slate-600">Upload Photos</p>
              </label>
            </div>
          </div>
          {formData.photos.length > 0 && (
            <div className="flex gap-2 flex-wrap mt-3">
              {formData.photos.map((photo, i) => (
                <div key={i} className="relative">
                  <img src={photo} alt="Preview" className="w-20 h-20 object-cover rounded" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Notes</label>
          <Textarea 
            data-testid="notes-input"
            value={formData.notes} 
            onChange={(e) => setFormData({...formData, notes: e.target.value})} 
            placeholder="Add any notes about this work..."
            rows={4}
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Customer Name</label>
          <Input 
            data-testid="customer-name-input"
            value={formData.customer_name} 
            onChange={(e) => setFormData({...formData, customer_name: e.target.value})} 
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Customer Details</label>
          <Textarea 
            data-testid="customer-details-input"
            value={formData.customer_details} 
            onChange={(e) => setFormData({...formData, customer_details: e.target.value})} 
          />
        </div>

        <div className="flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting} data-testid="cancel-worklog-btn">Cancel</Button>
          <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting} data-testid="submit-worklog-btn">
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </div>
    </form>
    </>
  );
};

const MessageForm = ({ task, onSubmit, onCancel }) => {
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      task_id: task.id,
      to_user_id: task.created_by,
      message
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle>Send Message to Admin</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 mt-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Task: {task.title}</label>
          <Textarea 
            data-testid="message-input"
            value={message} 
            onChange={(e) => setMessage(e.target.value)} 
            placeholder="Type your message..."
            rows={6}
            required
          />
        </div>
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-message-btn">Cancel</Button>
          <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="send-message-submit-btn">Send</Button>
        </div>
      </div>
    </form>
  );
};

export default FieldStaffDashboard;
